import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../services/api";
import { toast } from "sonner";
import { 
  Building2, 
  Key, 
  ArrowRight, 
  CheckCircle2, 
  Sparkles, 
  DollarSign, 
  ShieldCheck, 
  Camera, 
  Wrench, 
  Layers,
  MapPin
} from "lucide-react";

const LONDON_BOROUGHS = [
  "Kensington and Chelsea",
  "Westminster",
  "Islington",
  "Camden",
  "Canary Wharf",
  "Clapham",
  "Marylebone",
  "Mayfair",
  "Richmond upon Thames",
  "Hackney",
  "Southwark",
  "Hammersmith and Fulham",
  "Wandsworth",
  "Greenwich"
];

const PRESET_PHOTOS = [
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1600565193348-f74bd3c7ccdf?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=srgb&fm=jpg&q=85"
];

export const LetProperty = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submittedProperty, setSubmittedProperty] = useState(null);

  const [formData, setFormData] = useState({
    title: "Luxury Modern Residence in Chelsea",
    property_type: "Flat / Apartment",
    borough: "Kensington and Chelsea",
    postcode: "SW3 4RY",
    address_line: "28 King's Road, Chelsea",
    rent_pcm: 3200,
    deposit_amount: 3690,
    bedrooms: 2,
    bathrooms: 2,
    furnishing: "Furnished",
    available_from: "2026-07-01",
    description: "Immaculately presented modern home featuring open-plan designer kitchen, private balcony, concierge service, and secure underground parking.",
    key_features: ["Private Balcony", "Concierge Service", "Secure Parking", "High Speed Fibre"],
    images: PRESET_PHOTOS,
    pet_friendly: true,
    outside_space: true,
    parking: true,
    ideal_tenants: "Professional couples, corporate relocations seeking quiet Chelsea location.",
    requested_prep_services: ["Deep Clean", "EPC / Gas Cert"]
  });

  const toggleFeature = (feat) => {
    setFormData((prev) => {
      const exists = prev.key_features.includes(feat);
      return {
        ...prev,
        key_features: exists ? prev.key_features.filter((f) => f !== feat) : [...prev.key_features, feat]
      };
    });
  };

  const togglePrepService = (srv) => {
    setFormData((prev) => {
      const exists = prev.requested_prep_services.includes(srv);
      return {
        ...prev,
        requested_prep_services: exists 
          ? prev.requested_prep_services.filter((s) => s !== srv) 
          : [...prev.requested_prep_services, srv]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/properties", formData);
      setSubmittedProperty(res.data);
      toast.success("Property registered in FIND LET matching pool!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to list property. Please check your inputs.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FBFF] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        
        {/* Header */}
        <div className="text-center space-y-2 mb-8">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-[#D0E4FE] text-emerald-800 text-xs font-semibold">
            <Key className="w-3.5 h-3.5" />
            <span>Landlord Direct Intake Desk</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#091F5C] font-heading">
            List Your London Property (LET)
          </h1>
          <p className="text-[#4A5B78] text-sm max-w-lg mx-auto">
            Access pre-cleared London tenant briefs. No public listing noise, zero cold calls, pre-verified budgets.
          </p>
        </div>

        {submittedProperty ? (
          <div className="bg-white rounded-2xl p-8 border border-[#E5EBF5] shadow-xl text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-[#D0E4FE] text-[#334DAF] flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-[#091F5C] font-heading">
                Property Submitted for Concierge Verification
              </h2>
              <p className="text-sm text-[#4A5B78]">
                Reference: <span className="font-mono font-bold text-[#091F5C]">{submittedProperty.id?.slice(0, 8)}</span>
              </p>
              <p className="text-xs text-[#4A5B78] max-w-md mx-auto">
                Our operations team is now indexing your listing against active renter briefs. You will receive matched applicant inquiries in your Landlord Dashboard.
              </p>
            </div>

            <div className="pt-4 flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/landlord/dashboard"
                data-testid="view-landlord-dashboard-btn"
                className="px-6 py-3 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-semibold text-sm shadow-md"
              >
                Go to Landlord Dashboard
              </Link>
              <button
                onClick={() => setSubmittedProperty(null)}
                className="px-6 py-3 rounded-xl border border-[#D0E4FE] text-[#4A5B78] font-semibold text-sm"
              >
                List Another Property
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 sm:p-10 border border-[#E5EBF5] shadow-xl space-y-8">
            
            {/* Section 1: Specifications */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-[#091F5C] font-heading border-b pb-2 flex items-center space-x-2">
                <Building2 className="w-5 h-5 text-[#334DAF]" />
                <span>1. Property Specifications</span>
              </h3>

              <div>
                <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                  Property Title / Headline *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  data-testid="input-property-title"
                  className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:ring-2 focus:ring-[#334DAF]"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Property Type
                  </label>
                  <select
                    value={formData.property_type}
                    onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                    data-testid="select-property-type"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  >
                    <option value="Flat / Apartment">Flat / Apartment</option>
                    <option value="Penthouse">Penthouse</option>
                    <option value="Studio">Studio</option>
                    <option value="House">House</option>
                    <option value="Mansion Flat">Mansion Flat</option>
                    <option value="Mews House">Mews House</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Borough *
                  </label>
                  <select
                    value={formData.borough}
                    onChange={(e) => setFormData({ ...formData, borough: e.target.value })}
                    data-testid="select-property-borough"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  >
                    {LONDON_BOROUGHS.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Postcode *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.postcode}
                    onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                    data-testid="input-property-postcode"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm font-mono uppercase"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                  Full Address Line *
                </label>
                <input
                  type="text"
                  required
                  value={formData.address_line}
                  onChange={(e) => setFormData({ ...formData, address_line: e.target.value })}
                  data-testid="input-property-address"
                  className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Rent (£ PCM) *
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.rent_pcm}
                    onChange={(e) => setFormData({ ...formData, rent_pcm: Number(e.target.value) })}
                    data-testid="input-property-rent"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm font-mono font-bold"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Deposit (£) *
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.deposit_amount}
                    onChange={(e) => setFormData({ ...formData, deposit_amount: Number(e.target.value) })}
                    data-testid="input-property-deposit"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Bedrooms
                  </label>
                  <select
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({ ...formData, bedrooms: Number(e.target.value) })}
                    data-testid="select-property-bedrooms"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  >
                    <option value={1}>1 Bedroom</option>
                    <option value={2}>2 Bedrooms</option>
                    <option value={3}>3 Bedrooms</option>
                    <option value={4}>4+ Bedrooms</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Bathrooms
                  </label>
                  <select
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({ ...formData, bathrooms: Number(e.target.value) })}
                    data-testid="select-property-bathrooms"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  >
                    <option value={1}>1 Bathroom</option>
                    <option value={2}>2 Bathrooms</option>
                    <option value={3}>3+ Bathrooms</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Furnishing Status
                  </label>
                  <select
                    value={formData.furnishing}
                    onChange={(e) => setFormData({ ...formData, furnishing: e.target.value })}
                    data-testid="select-property-furnishing"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  >
                    <option value="Furnished">Furnished (Turnkey)</option>
                    <option value="Unfurnished">Unfurnished</option>
                    <option value="Flexible">Flexible</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Available From Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.available_from}
                    onChange={(e) => setFormData({ ...formData, available_from: e.target.value })}
                    data-testid="input-property-available-from"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                  Detailed Description *
                </label>
                <textarea
                  rows={3}
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="input-property-description"
                  className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                />
              </div>
            </div>

            {/* Section 2: Features & Lifestyle */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-bold text-[#091F5C] font-heading border-b pb-2 flex items-center space-x-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                <span>2. Key Features & Tenant Criteria</span>
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className={`p-3.5 rounded-xl border cursor-pointer flex items-center space-x-3 transition-colors ${
                  formData.pet_friendly ? "bg-[#E8F2FE] border-[#D0E4FE] font-semibold text-emerald-950" : "border-[#E5EBF5]"
                }`}>
                  <input
                    type="checkbox"
                    checked={formData.pet_friendly}
                    onChange={(e) => setFormData({ ...formData, pet_friendly: e.target.checked })}
                    data-testid="landlord-checkbox-pet-friendly"
                    className="w-4 h-4 text-[#334DAF] rounded"
                  />
                  <span className="text-xs">Pet Friendly Allowed</span>
                </label>

                <label className={`p-3.5 rounded-xl border cursor-pointer flex items-center space-x-3 transition-colors ${
                  formData.outside_space ? "bg-[#E8F2FE] border-[#D0E4FE] font-semibold text-emerald-950" : "border-[#E5EBF5]"
                }`}>
                  <input
                    type="checkbox"
                    checked={formData.outside_space}
                    onChange={(e) => setFormData({ ...formData, outside_space: e.target.checked })}
                    data-testid="landlord-checkbox-outside-space"
                    className="w-4 h-4 text-[#334DAF] rounded"
                  />
                  <span className="text-xs">Garden / Roof Terrace</span>
                </label>

                <label className={`p-3.5 rounded-xl border cursor-pointer flex items-center space-x-3 transition-colors ${
                  formData.parking ? "bg-[#E8F2FE] border-[#D0E4FE] font-semibold text-emerald-950" : "border-[#E5EBF5]"
                }`}>
                  <input
                    type="checkbox"
                    checked={formData.parking}
                    onChange={(e) => setFormData({ ...formData, parking: e.target.checked })}
                    data-testid="landlord-checkbox-parking"
                    className="w-4 h-4 text-[#334DAF] rounded"
                  />
                  <span className="text-xs">Parking Included</span>
                </label>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                  Ideal Tenant Preferences
                </label>
                <input
                  type="text"
                  placeholder="e.g. Professional couples, corporate relocations"
                  value={formData.ideal_tenants}
                  onChange={(e) => setFormData({ ...formData, ideal_tenants: e.target.value })}
                  data-testid="input-ideal-tenants"
                  className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                />
              </div>
            </div>

            {/* Section 3: Optional Property Preparation Add-ons */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between border-b pb-2">
                <h3 className="text-lg font-bold text-[#091F5C] font-heading flex items-center space-x-2">
                  <Wrench className="w-5 h-5 text-[#334DAF]" />
                  <span>3. Optional Property Preparation Services</span>
                </h3>
                <span className="text-xs font-semibold px-2 py-0.5 rounded bg-[#D0E4FE] text-blue-800">
                  FIND LET Prep
                </span>
              </div>
              <p className="text-xs text-[#4A5B78]">
                Tick any services you'd like our concierge team to dispatch prior to tenant move-in:
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {["Deep Clean", "EPC / Gas Cert", "Inventory Check", "Minor Repairs"].map((srv) => {
                  const isChecked = formData.requested_prep_services.includes(srv);
                  return (
                    <button
                      type="button"
                      key={srv}
                      onClick={() => togglePrepService(srv)}
                      data-testid={`prep-service-${srv.replace(/[^a-zA-Z]/g, "-").toLowerCase()}`}
                      className={`p-3 rounded-xl border text-xs font-medium text-left transition-all ${
                        isChecked 
                          ? "bg-[#E8F2FE] border-[#D0E4FE] text-blue-900 font-bold shadow-sm"
                          : "border-[#E5EBF5] hover:border-[#D0E4FE] text-[#4A5B78]"
                      }`}
                    >
                      <span>{srv}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="pt-6 border-t flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-property-listing-btn"
                className="px-8 py-3.5 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-bold text-sm shadow-lg shadow-emerald-700/30 transition-all flex items-center space-x-2 disabled:opacity-50"
              >
                <Key className="w-4 h-4" />
                <span>{submitting ? "Registering Property..." : "Submit Property Listing (LET)"}</span>
              </button>
            </div>

          </form>
        )}

      </div>
    </div>
  );
};
